import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2, ArrowLeft, AlertTriangle, BookOpen, Calendar, User, ShieldCheck } from 'lucide-react';
import { ContentBlock } from './BlogBlockEditor';
import { getBlogPostBySlug } from '../services/apiService';
import { BlogPost } from '../types';

const IS_DEMO = process.env.REACT_APP_IS_DEMO === 'true';

function renderContent(content: string) {
    if (!content) return null;
    if (!content.trim().startsWith('[')) {
        // Legacy HTML content (demo posts)
        return (
            <div
                className="prose prose-lg prose-headings:font-serif prose-headings:text-primary prose-a:text-brand max-w-none text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: content }}
            />
        );
    }
    try {
        const blocks: ContentBlock[] = JSON.parse(content);
        return (
            <div className="space-y-5">
                {blocks.map((block, i) => {
                    if (block.type === 'heading') {
                        return <h2 key={i} className="text-2xl md:text-3xl font-serif font-bold text-primary mt-8 mb-2">{block.text}</h2>;
                    }
                    if (block.type === 'paragraph') {
                        return <p key={i} className="text-gray-700 leading-relaxed text-lg">{block.text}</p>;
                    }
                    if (block.type === 'list') {
                        return (
                            <ul key={i} className="space-y-2 pl-2">
                                {block.items.map((item, j) => (
                                    <li key={j} className="flex items-start gap-3 text-gray-700 text-lg">
                                        <span className="text-brand font-bold mt-1 shrink-0">•</span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        );
                    }
                    if (block.type === 'quote') {
                        return (
                            <blockquote key={i} className="border-l-4 border-brand pl-6 italic text-gray-600 my-6 text-xl leading-relaxed">
                                {block.text}
                            </blockquote>
                        );
                    }
                    return null;
                })}
            </div>
        );
    } catch {
        return <p className="text-gray-700">{content}</p>;
    }
}

const BlogPostDetail = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [post, setPost] = useState<BlogPost | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!slug) return;
        getBlogPostBySlug(slug)
            .then(data => {
                if (data) setPost(data);
                else setNotFound(true);
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-20">
                <Loader2 className="w-10 h-10 text-brand animate-spin" />
            </div>
        );
    }

    if (notFound || !post) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center pt-20 gap-4 text-gray-400">
                <BookOpen className="w-12 h-12 opacity-50" />
                <p className="font-medium text-lg">Article not found.</p>
                <button onClick={() => navigate('/blog')} className="text-brand font-bold hover:underline flex items-center gap-2">
                    <ArrowLeft className="w-4 h-4" /> Back to Blog
                </button>
            </div>
        );
    }

    const siteUrl = 'https://www.anotherhome.us';
    const postUrl = `${siteUrl}/blog/${post.slug}`;
    const ogImage = post.coverImageUrl || `${siteUrl}/img/og-image.jpg`;

    return (
        <div className="pt-28 pb-24 animate-fade-in">

            <Helmet>
                <title>{post.title} | Another Home Blog</title>
                <meta name="description" content={post.excerpt || post.title} />
                <link rel="canonical" href={postUrl} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={postUrl} />
                <meta property="og:title" content={post.title} />
                <meta property="og:description" content={post.excerpt || post.title} />
                <meta property="og:image" content={ogImage} />
                {post.createdAt && <meta property="article:published_time" content={new Date(post.createdAt).toISOString()} />}
                {post.author && <meta property="article:author" content={post.author} />}
                {post.category && <meta property="article:section" content={post.category} />}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={post.title} />
                <meta name="twitter:description" content={post.excerpt || post.title} />
                <meta name="twitter:image" content={ogImage} />
            </Helmet>

            {/* COVER IMAGE */}
            {post.coverImageUrl && (
                <div className="relative w-full h-[420px] mb-0 overflow-hidden">
                    <img
                        src={post.coverImageUrl}
                        alt={post.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                </div>
            )}

            <div className="max-w-3xl mx-auto px-6 md:px-8">

                {/* BACK BUTTON */}
                <button
                    onClick={() => navigate('/blog')}
                    className="flex items-center gap-2 text-brand font-bold hover:gap-3 transition-all mt-8 mb-6 text-sm"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to Blog
                </button>

                {/* DEMO BANNER */}
                {IS_DEMO && (
                    <div className="mb-8 flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl px-6 py-4 text-sm font-medium">
                        <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-amber-500" />
                        <span>
                            <strong>Preview Mode</strong> — This is a sample article. Real content will be published at launch.
                        </span>
                    </div>
                )}

                {/* META */}
                {post.category && (
                    <span className="inline-block bg-brand/10 text-brand text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
                        {post.category}
                    </span>
                )}

                <h1 className="text-4xl md:text-5xl font-serif font-bold text-primary leading-tight mb-6">
                    {post.title}
                </h1>

                <div className="flex items-center gap-5 text-sm text-gray-400 font-medium mb-10 pb-8 border-b border-gray-100">
                    <span className="flex items-center gap-1.5">
                        <User className="w-4 h-4" /> {post.author}
                    </span>
                    {post.createdAt && (
                        <span className="flex items-center gap-1.5">
                            <Calendar className="w-4 h-4" />
                            {new Date(post.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </span>
                    )}
                </div>

                {/* EXCERPT */}
                {post.excerpt && (
                    <p className="text-xl text-gray-600 leading-relaxed mb-8 font-light italic border-l-4 border-brand pl-6">
                        {post.excerpt}
                    </p>
                )}

                {/* CONTENT */}
                {renderContent(post.content)}

                {/* CERTIFICATION — subtle footer row */}
                {post.certificationVerifyUrl && (
                    <div className="mt-16 pt-6 border-t border-gray-100 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2 text-gray-400">
                            <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                            <span className="text-xs">
                                Content certified by Axiomgroup
                                {post.certificationDate && (
                                    <> · {new Date(post.certificationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</>
                                )}
                            </span>
                            <a
                                href={post.certificationVerifyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-gray-400 hover:text-brand transition-colors underline underline-offset-2"
                            >
                                Verify
                            </a>
                        </div>

                        <a
                            href={post.certificationVerifyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Scan to verify certification"
                        >
                            <img
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=64x64&data=${encodeURIComponent(post.certificationVerifyUrl)}&color=9ca3af&bgcolor=ffffff`}
                                alt="Certification QR"
                                className="w-10 h-10 opacity-50 hover:opacity-100 transition-opacity"
                            />
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogPostDetail;
